// How a column reads to the user: the title its expanded form collapses into,
// and the caption under a difference form. Pure functions over a column and the
// schema for its type — the drawer's working state lives in use-column-editor.

import type { EditorColumn, FieldGroup, ShowAs, TypeSchema } from './types'

// Separates the two operands of a difference column, both in the expanded form
// and in the title it collapses into.
export const COMPARISON_OPERATOR = '<>'

// How each `show_as` mode is presented: the symbol that goes in the collapsed
// row title, and the caption key that spells out the subtraction direction.
// Both readings of a mode live here so they can't drift apart.
interface ShowAsPresentation {
  symbol: string
  captionKey: string
}

// The schema field these presentations key off — the one field key the drawer
// knows by name, kept next to what it means.
export const SHOW_AS_FIELD_KEY = 'show_as'

const SHOW_AS_PRESENTATION: Record<ShowAs, ShowAsPresentation> = {
  absolute: { symbol: '', captionKey: 'difference.absolute' },
  percentage: { symbol: '%', captionKey: 'difference.percentage' },
  ratio: { symbol: '/', captionKey: 'difference.ratio' },
}

// Declared over ShowAs so a mode added to the union has to be given a
// presentation, read through a partial view so a *stored* value outside the
// union reads as the miss it is rather than as a guaranteed hit.
const showAsLookup: Partial<Record<string, ShowAsPresentation>> =
  SHOW_AS_PRESENTATION

// A mode we have no presentation for gets none: the server can add a fourth
// `show_as` option without this file knowing, and reading it as `absolute`
// would caption it "subtracts A from B" — wrong rather than merely absent.
export const showAsPresentation = (
  value: string | undefined
): ShowAsPresentation | undefined =>
  value === undefined ? undefined : showAsLookup[value]

// The human-readable pill title, assembled from the labels of the currently
// selected options — mirroring ReportColumn#name in the legacy report editor.
//
// Walks the schema's fields in the order the server ships them, the same source
// the form rows render from, so a field the drawer lets you edit can't be
// missing from the title it collapses into. `show_as` is the one field that
// reads as a symbol rather than a label, and the operand boundary the schema
// marks with its 'a'/'b' groups becomes the comparison operator.
export const buildColumnTitle = (
  column: EditorColumn,
  typeSchema: TypeSchema | undefined,
  spacerLabel: string
): string => {
  if (column.type === 'ColumnSpacer') return spacerLabel

  const parts: string[] = []
  let previousGroup: FieldGroup | null = null

  typeSchema?.fields.forEach((field) => {
    if (previousGroup === 'a' && field.group === 'b') {
      parts.push(COMPARISON_OPERATOR)
    }
    previousGroup = field.group

    const value = column.values[field.key]

    if (field.key === SHOW_AS_FIELD_KEY) {
      parts.push(showAsPresentation(value)?.symbol ?? '')
      return
    }

    // A stored value can sit outside its field's option list — a column whose
    // `show` is plain "budget" after that budget set was deleted, say, while
    // show_options only offers "actuals" and the surviving "budget-<id>"s.
    // There is no label to print for it, and the raw value is an internal
    // token, so an unmatched value contributes nothing to the title. The form
    // it expands into says the same thing out loud (column-field-select).
    parts.push(
      field.options.find((option) => option.value === value)?.label ?? ''
    )
  })

  return parts.filter(Boolean).join(' ')
}
